/**
 * rqc.ts — 출하조회(RQC) 도메인 타입.
 *
 * Firestore 컬렉션:
 *   - "rqc_requests" : 클라이언트가 doc 생성하면 rqcFetchOnDemand CF가 트리거됨.
 *                      CF가 RQC 서버에서 가져와 daily_shipment 저장 + 본 doc에 status 업데이트.
 *   - "daily_shipment" : RQC raw 레코드. doc id = `${date}_${idx}` (3자리 pad).
 *
 * tier 분류는 CF가 채워줌. 누락 시 클라이언트 폴백.
 */
export type RqcTier = "gov" | "cust";

export interface RqcShipment {
  /** Firestore 문서 ID (`${date}_${idx}`) */
  id: string;
  date: string;
  idx?: number;
  /** view.jsp 호출용 1-base 인덱스. 누락 시 (idx+1) 폴백 */
  sc?: number;
  company: string;
  site: string;
  kind: string;
  code: string;
  mix: string;
  qty: number;
  shipped: number;
  remain: number;
  cumcars: number;
  firstcar: string;
  lastcar: string;
  memo1: string;
  status?: string;
  /** CF가 채움. 누락 시 클라이언트 폴백 */
  tier?: RqcTier;
  /** 거래처 코드 (yejung.scode) — C023=관급. SQL 전환 후 채워짐(옛 데이터엔 없음) */
  scode?: string;
  /** 현장 코드 (yejung.hcode) — songjang 명세 조회 키 */
  hcode?: string;
  /** 규격 코드 (yejung.bcode) */
  bcode?: string;
}

/** view.jsp 1행 = 1대 차량의 1회 출하 송장 */
export interface ShipmentDetail {
  seq: number;      // 1-base 순번
  time: string;     // HH:MM
  carNo: string;    // 차번
  driver: string;   // 기사명
  qty: number;      // 당회 출하량(㎥)
  cumQty: number;   // 누적 출하량(㎥)
  status: string;   // 상태 ("A" 등)
}

export type RqcRequestStatus = "pending" | "done" | "error";

/** rqcFetchOnDemand 요청 문서 — 클라이언트는 date/status/requested_*만 set */
export interface RqcRequest {
  date: string;
  status: RqcRequestStatus;
  written?: number;
  error?: string;
}

/** 관급 분류 키워드 — CF GOV_KEYWORDS와 동일. tier 미설정 레코드의 클라이언트 폴백용 */
const GOV_KEYWORDS = [
  "관급", "공공", "국가", "지자체", "시청", "군청",
  "레미콘공업협동조합", "공업협동조합", "전남레미콘", "광주.전남레",
];

/**
 * 관급/사급 분류. 우선순위: scode(C023) > CF tier > 키워드 폴백.
 * SQL 전환 후 scode 가 가장 권위 있는 분류 키(영업과 동일 규칙).
 */
export function classifyTier(s: Pick<RqcShipment, "tier" | "company" | "site" | "scode">): RqcTier {
  if (s.scode) return s.scode === "C023" ? "gov" : "cust";
  if (s.tier === "gov" || s.tier === "cust") return s.tier;
  const t = `${s.company ?? ""} ${s.site ?? ""}`;
  return GOV_KEYWORDS.some((kw) => t.includes(kw)) ? "gov" : "cust";
}

/** KST 오늘 yyyy-MM-dd */
export function kstToday(): string {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
